import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { useDevice } from '@/contexts/DeviceContext';
import { useAuth } from '@/contexts/AuthContext';
import { Header } from '@/components/Header';
import { FlowNavigation } from '@/components/FlowNavigation';
import './SeatSelection.scss';

// 좌석 타입 정의 (Admin의 SeatItem 참고)
interface Seat {
    id: string;
    type: 'seat' | 'structure';
    seatNumber?: number;
    structureName?: string;
    x: number;
    y: number;
    width: number;
    height: number;
    status?: 'available' | 'unavailable' | 'selected'; // 좌석 상태
}

export function SeatSelection() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const { isMobile } = useDevice();
    const { user, isLoading: authLoading } = useAuth();
    const [seats, setSeats] = useState<Seat[]>([]);
    const [selectedSeatId, setSelectedSeatId] = useState<string | null>(null);

    // URL 파라미터에서 시간대 정보 가져오기
    const timeSlotId = searchParams.get('timeSlotId');
    const timeSlotLabel = location.state?.timeSlotLabel || '';

    // 초기 데이터 로드
    useEffect(() => {
        if (!authLoading && (user?.fixedStudySeats?.length ?? 0) > 0) {
            navigate('/study-cafe/fixed-seat', { replace: true });
            return;
        }

        if (!timeSlotId) {
            // 시간대를 선택하지 않고 직접 접근한 경우 시간 선택 페이지로 리다이렉트
            navigate('/study-cafe');
            return;
        }
        fetchSeats();
    }, [authLoading, navigate, timeSlotId, user]);

    const fetchSeats = async () => {
        try {
            // timeSlotId를 전달하여 해당 시간대의 예약 현황을 반영한 좌석 목록 조회
            const url = timeSlotId
                ? `/api/center/study-seats?timeSlotId=${timeSlotId}`
                : '/api/center/study-seats';
            const response = await fetch(url);
            if (response.ok) {
                const data = await response.json();

                // API 데이터를 Seat 타입으로 변환 (Admin의 로직 참고)
                const fetchedSeats: Seat[] = data.map((item: any) => {
                    const isSeat = !isNaN(Number(item.seatName));
                    return {
                        id: item.id.toString(),
                        type: isSeat ? 'seat' : 'structure',
                        seatNumber: isSeat ? Number(item.seatName) : undefined,
                        structureName: isSeat ? undefined : item.seatName,
                        x: item.positionX,
                        y: item.positionY,
                        width: item.width || (isSeat ? 44 : 80),
                        height: item.height || 44,
                        status: item.isAvailable ? 'available' : 'unavailable'
                    };
                });

                setSeats(fetchedSeats);
            } else {
                console.error('Failed to fetch seats:', response.status);
            }
        } catch (error) {
            console.error('Error fetching seats:', error);
        }
    };

    // 좌석 클릭 핸들러
    const handleSeatClick = (seat: Seat) => {
        if (seat.type !== 'seat' || seat.status === 'unavailable') return;

        if (selectedSeatId === seat.id) {
            // 이미 선택된 좌석 클릭 시 해제
            setSelectedSeatId(null);
            updateSeatStatus(seat.id, 'available');
        } else {
            // 다른 좌석 선택 (기존 선택 해제 후 새 좌석 선택)
            if (selectedSeatId) {
                updateSeatStatus(selectedSeatId, 'available');
            }
            setSelectedSeatId(seat.id);
            updateSeatStatus(seat.id, 'selected');
        }
    };

    const updateSeatStatus = (id: string, status: 'available' | 'unavailable' | 'selected') => {
        setSeats(prev => prev.map(s => s.id === id ? { ...s, status } : s));
    };

    const handleNext = async () => {
        if (!selectedSeatId || !timeSlotId) return;

        try {
            const response = await fetch('/api/center/study-seats/reservations', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    timeSlotId: Number(timeSlotId),
                    seatId: Number(selectedSeatId),
                }),
            });

            if (response.ok) {
                const reservationData = await response.json(); // ReservationResponseDto

                navigate('/study-cafe/complete', {
                    state: {
                        reservationData,
                        // 기존 정보도 혹시 모르니 유지하거나, reservationData로 충분하면 대체
                        seatName: reservationData.seatNumber,
                        time: `${reservationData.timeSlotStart.substring(0, 5)} ~ ${reservationData.timeSlotEnd.substring(0, 5)}`
                    }
                });
            } else {
                // 에러 메시지 처리 (백엔드에서 메시지를 줄 경우)
                const errorData = await response.json().catch(() => ({}));
                const errorMessage = errorData.message || '예약에 실패했습니다.';
                alert(errorMessage);
            }
        } catch (error) {
            console.error('Reservation failed:', error);
            alert('예약 중 오류가 발생했습니다.');
        }
    };

    return (
        <div className={`seat-selection-page ${isMobile ? 'seat-selection-page--mobile' : ''}`}>
            {/* 공통 헤더 */}
            <Header />

            <main className="seat-selection-main">
                <div className="main-top">
                    <div className="title-area">
                        <h1 className="page-title">스터디카페 좌석 선택</h1>
                        {timeSlotLabel && (
                            <p className="page-subtitle">선택 시간: {timeSlotLabel}</p>
                        )}
                    </div>

                    {/* 범례 */}
                    <div className="legend">
                        <div className="legend__item">
                            <span className="legend__color legend__color--unavailable-seat"></span>
                            <span>: 예약 불가</span>
                        </div>
                        <div className="legend__item">
                            <span className="legend__color legend__color--selected-seat"></span>
                            <span>: 선택 좌석</span>
                        </div>
                    </div>
                </div>

                {/* 좌석 배치도 캔버스 (Zoom/Pan 적용) */}
                <div className="seat-canvas-container">
                    <TransformWrapper
                        initialScale={isMobile ? 1 : 1.2}
                        minScale={0.3}
                        maxScale={2}
                        wheel={{ step: 0.1 }}
                        disabled={false}
                        limitToBounds={false}
                    >
                        <TransformComponent
                            wrapperClass="seat-transform-wrapper"
                        >
                            <div className="seat-canvas">
                                {seats.map((item) => (
                                    <div
                                        key={item.id}
                                        className={`
                                            canvas-item
                                            canvas-item--${item.type}
                                            ${item.type === 'seat' ? `canvas-item--${item.status}` : ''}
                                        `}
                                        style={{
                                            left: item.x,
                                            top: item.y,
                                            width: item.width,
                                            height: item.height,
                                        }}
                                        onClick={() => {
                                            handleSeatClick(item);
                                        }}
                                        onTouchEnd={() => {
                                            // 터치 디바이스 호환성
                                        }}
                                    >
                                        {item.type === 'seat' ? item.seatNumber : item.structureName}
                                    </div>
                                ))}
                            </div>
                        </TransformComponent>
                    </TransformWrapper>
                    <p className="seat-canvas-info">※ 화면 '이동/확대/축소'가 가능합니다</p>
                </div>
            </main>

            {/* 하단 네비게이션 */}
            <FlowNavigation
                onPrev={() => navigate('/study-cafe')}
                onNext={handleNext}
                nextLabel="예약"
                hideNextArrow
                isNextDisabled={!selectedSeatId}
            />
        </div>
    );
}
